import React, { useEffect, useState } from 'react';
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    IconButton,
    InputLabel,
    MenuItem,
    Select,
    TextField,
} from '@mui/material';

import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';

import { useTranslation } from 'react-i18next';

import { productionMethodAPI } from '../../services/apiService';

import './FormulationDialog.css';

const UNIT_OPTIONS = [
    { value: 'kg', labelKey: 'unit_kg' },
    { value: 'g', labelKey: 'unit_g' },
    { value: 'liter', labelKey: 'unit_liter' },
    { value: 'ml', labelKey: 'unit_ml' },
    { value: 'piece', labelKey: 'unit_piece' },
    { value: 'pack', labelKey: 'unit_pack' },
];

const EMPTY_FORM = {
    name: '',
    output_name: '',
    output_unit: '',
    output_unit_value: '1',
};

function FormulationDialog({
    open,
    onClose,
    onSaved,
    inventories = [],
    editingFormulation = null,
}) {
    const { t } = useTranslation();

    const [form, setForm] = useState(EMPTY_FORM);
    const [ingredients, setIngredients] = useState([]);
    const [saving, setSaving] = useState(false);

    const isEditing = Boolean(editingFormulation);

    const getUnitLabel = (unit) => {
        const found = UNIT_OPTIONS.find((item) => item.value === unit);

        return found ? t(found.labelKey) : unit || '-';
    };

    useEffect(() => {
        if (!open) return;

        if (!editingFormulation) {
            setForm(EMPTY_FORM);
            setIngredients([]);
            return;
        }

        setForm({
            name: editingFormulation.name || '',
            output_name: editingFormulation.output_name || '',
            output_unit: editingFormulation.output_unit || '',
            output_unit_value: editingFormulation.output_unit_value ?? '1',
        });

        const loadLines = async () => {
            try {
                const lines = await productionMethodAPI.getLines(editingFormulation.id, {
                    force: true,
                    fallback: [],
                });

                setIngredients(
                    Array.isArray(lines)
                        ? lines.map((line) => ({
                              id: line.id,
                              ingredient: line.ingredient,
                              quantity: line.quantity,
                          }))
                        : []
                );
            } catch (error) {
                console.error(error);
                setIngredients([]);
            }
        };

        loadLines();
    }, [open, editingFormulation]);

    const handleClose = () => {
        if (saving) return;

        onClose();
    };

    const handleFormChange = (event) => {
        const { name, value } = event.target;

        setForm((previous) => ({
            ...previous,
            [name]: value,
        }));
    };

    const addIngredient = () => {
        setIngredients((previous) => [
            ...previous,
            {
                id: null,
                ingredient: '',
                quantity: '',
            },
        ]);
    };

    const updateIngredient = (index, field, value) => {
        setIngredients((previous) =>
            previous.map((item, itemIndex) =>
                itemIndex === index
                    ? {
                          ...item,
                          [field]: value,
                      }
                    : item
            )
        );
    };

    const removeIngredient = (index) => {
        setIngredients((previous) => previous.filter((_, itemIndex) => itemIndex !== index));
    };

    const validate = () => {
        if (
            !form.name.trim() ||
            !form.output_name.trim() ||
            !form.output_unit ||
            !form.output_unit_value
        ) {
            return t('fill_required_fields');
        }
        if (Number(form.output_unit_value) <= 0) {
            return t('formulation_invalid_output');
        }
        if (!ingredients.length) {
            return t('formulation_add_ingredient');
        }
        const hasInvalidIngredient = ingredients.some(
            (ingredient) =>
                !ingredient.ingredient ||
                ingredient.quantity === '' ||
                Number(ingredient.quantity) <= 0
        );
        if (hasInvalidIngredient) {
            return t('formulation_invalid_ingredient');
        }
        const ingredientIds = ingredients.map((ingredient) => Number(ingredient.ingredient));
        const hasDuplicateIngredient = new Set(ingredientIds).size !== ingredientIds.length;

        if (hasDuplicateIngredient) {
            return t('formulation_duplicate_ingredient');
        }

        return null;
    };

    const handleSave = async () => {
        const validationError = validate();

        if (validationError) {
            if (onSaved) {
                onSaved({
                    success: false,
                    message: validationError,
                });
            }

            return;
        }

        try {
            setSaving(true);

            const payload = {
                name: form.name.trim(),
                output_name: form.output_name.trim(),
                output_unit: form.output_unit,
                output_unit_value: Number(form.output_unit_value),
            };

            let method;

            if (isEditing) {
                method = await productionMethodAPI.update(editingFormulation.id, payload);
            } else {
                method = await productionMethodAPI.create(payload);
            }

            const methodId = method?.id || editingFormulation?.id;

            if (!methodId) {
                throw new Error('Production method ID was not returned.');
            }
            if (isEditing) {
                const existingLines = await productionMethodAPI.getLines(methodId, {
                    force: true,
                    fallback: [],
                });

                await Promise.all(
                    existingLines.map((line) => productionMethodAPI.removeLine(line.id))
                );
            }

            await Promise.all(
                ingredients.map((ingredient) =>
                    productionMethodAPI.createLine({
                        method: methodId,
                        ingredient: Number(ingredient.ingredient),
                        quantity: Number(ingredient.quantity),
                    })
                )
            );

            if (onSaved) {
                await onSaved({
                    success: true,
                    message: isEditing
                        ? t('formulation_update_success')
                        : t('formulation_create_success'),
                });
            }
        } catch (error) {
            console.error(error);

            if (onSaved) {
                onSaved({
                    success: false,
                    message: error.message || t('formulation_save_error'),
                });
            }
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="md"
            fullWidth
            className="formulation_dialog"
        >
            <DialogTitle>{isEditing ? t('edit_formulation') : t('new_formulation')}</DialogTitle>

            <DialogContent>
                <div className="formulation_dialog_form">
                    <TextField
                        label={t('formulation_name')}
                        name="name"
                        value={form.name}
                        onChange={handleFormChange}
                        fullWidth
                        margin="dense"
                    />

                    <TextField
                        label={t('formulation_output_name')}
                        name="output_name"
                        value={form.output_name}
                        onChange={handleFormChange}
                        fullWidth
                        margin="dense"
                    />

                    <div className="formulation_output_row">
                        <FormControl fullWidth>
                            <InputLabel>{t('unit')}</InputLabel>

                            <Select
                                name="output_unit"
                                value={form.output_unit}
                                label={t('unit')}
                                onChange={handleFormChange}
                            >
                                {UNIT_OPTIONS.map((unit) => (
                                    <MenuItem key={unit.value} value={unit.value}>
                                        {t(unit.labelKey)}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <TextField
                            label={t('formulation_output_amount')}
                            name="output_unit_value"
                            type="number"
                            value={form.output_unit_value}
                            onChange={handleFormChange}
                            fullWidth
                            inputProps={{
                                min: 0.0001,
                                step: 'any',
                            }}
                        />
                    </div>

                    <div className="formulation_dialog_section_header">
                        <div>
                            <h3>{t('formulation_ingredients')}</h3>

                            <p>{t('formulation_ingredients_description')}</p>
                        </div>

                        <Button variant="outlined" startIcon={<AddIcon />} onClick={addIngredient}>
                            {t('add_ingredient')}
                        </Button>
                    </div>

                    <div className="formulation_dialog_lines">
                        {ingredients.length === 0 ? (
                            <div className="formulation_no_lines">
                                {t('formulation_add_ingredient')}
                            </div>
                        ) : (
                            ingredients.map((ingredient, index) => {
                                const selectedInventory = inventories.find(
                                    (item) => item.id === Number(ingredient.ingredient)
                                );

                                return (
                                    <div
                                        className="formulation_dialog_line"
                                        key={ingredient.id || `new-${index}`}
                                    >
                                        <FormControl fullWidth>
                                            <InputLabel>{t('ingredient')}</InputLabel>

                                            <Select
                                                value={ingredient.ingredient}
                                                label={t('ingredient')}
                                                onChange={(event) =>
                                                    updateIngredient(
                                                        index,
                                                        'ingredient',
                                                        event.target.value
                                                    )
                                                }
                                            >
                                                {inventories.map((item) => (
                                                    <MenuItem key={item.id} value={item.id}>
                                                        <div className="formulation_inventory_option">
                                                            <span>{item.name}</span>

                                                            <small>
                                                                {item.quantity}{' '}
                                                                {getUnitLabel(item.unit)}
                                                            </small>
                                                        </div>
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>

                                        <TextField
                                            label={t('quantity_per_unit')}
                                            type="number"
                                            value={ingredient.quantity}
                                            onChange={(event) =>
                                                updateIngredient(
                                                    index,
                                                    'quantity',
                                                    event.target.value
                                                )
                                            }
                                            inputProps={{
                                                min: 0.0001,
                                                step: 'any',
                                            }}
                                            fullWidth
                                        />

                                        <div className="formulation_line_unit">
                                            {selectedInventory
                                                ? getUnitLabel(selectedInventory.unit)
                                                : '-'}
                                        </div>

                                        <IconButton
                                            color="error"
                                            onClick={() => removeIngredient(index)}
                                            disabled={saving}
                                        >
                                            <DeleteIcon />
                                        </IconButton>
                                    </div>
                                );
                            })
                        )}
                    </div>
                </div>
            </DialogContent>

            <DialogActions>
                <Button onClick={handleClose} disabled={saving}>
                    {t('cancel')}
                </Button>

                <Button variant="contained" onClick={handleSave} disabled={saving}>
                    {saving ? t('loading') : t('save')}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

export default FormulationDialog;
